import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Typography,
  Box,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { format } from "date-fns";

const LogTable = ({
  logs,
  pagination,
  onRowClick,
  onSortChange,
  sortOrder,
  onPageChange,
  isLoading,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(pagination.page - 1);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    onPageChange(event, newPage + 1);
  };

  const handleSortChange = (field) => {
    const isAsc = sortOrder.field === field && sortOrder.order === "asc";
    onSortChange(field, isAsc ? "desc" : "asc");
  };

  // Helper to style Status Chips
  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("deliver") || s.includes("sent")) return "success";
    if (s.includes("fail") || s.includes("bounce") || s.includes("drop"))
      return "error";
    if (s.includes("defer") || s.includes("queue")) return "warning";
    return "default";
  };

  // Helper to style Type Chips
  const getTypeLabel = (type) => {
    if (type === "Cloud") return { label: "Incoming", color: "info" };
    if (type === "Relay") return { label: "Outgoing", color: "secondary" };
    if (type === "DKIM") return { label: "Signing", color: "success" };
    return { label: type || "Unknown", color: "default" };
  };

  return (
    <>
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                "& th": { fontWeight: 700, color: theme.palette.text.primary },
              }}
            >
              {[
                { id: "log_timestamp", label: "Date & Time", width: "180px" },
                { id: "from_email_id", label: "From Email", width: "250px" },
                { id: "subject", label: "Subject", width: "1fr" },
                { id: "status", label: "Status", width: "120px" },
                { id: "type", label: "Type", width: "110px" },
              ].map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sx={{
                    width: headCell.width,
                    minWidth: headCell.width,
                  }}
                >
                  <TableSortLabel
                    active={sortOrder.field === headCell.id}
                    direction={
                      sortOrder.field === headCell.id ? sortOrder.order : "asc"
                    }
                    onClick={() => handleSortChange(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ width: "80px", minWidth: "80px" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs?.map((log) => {
              const typeInfo = getTypeLabel(log.type);
              return (
                <TableRow
                  key={log.euid}
                  hover
                  sx={{
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                      width: "140px",
                      minWidth: "140px",
                    }}
                  >
                    {log.log_timestamp
                      ? format(
                          new Date(log.log_timestamp),
                          "MMM dd, yyyy hh:mm a"
                        )
                      : "-"}
                  </TableCell>

                  <TableCell sx={{ width: "250px", minWidth: "180px" }}>
                    <Tooltip title={log.from_email_id}>
                      <Typography
                        sx={{
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                          display: "block",
                        }}
                        variant="body2"
                        noWrap
                      >
                        {log.from_email_id}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    sx={{
                      flex: 1,
                      minWidth: "300px",
                      paddingRight: 2,
                    }}
                  >
                    <Tooltip title={log.subject}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          display: "block",
                        }}
                      >
                        {log.subject}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ width: "120px", minWidth: "120px" }}>
                    <Chip
                      label={log.status}
                      color={getStatusColor(log.status)}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, borderRadius: "6px" }}
                    />
                  </TableCell>

                  <TableCell sx={{ width: "110px", minWidth: "110px" }}>
                    <Chip
                      label={typeInfo.label}
                      color={typeInfo.color}
                      size="small"
                      sx={{ fontWeight: 500, borderRadius: "6px", height: 24 }}
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ width: "80px", minWidth: "80px" }}
                  >
                    <Tooltip title="View Details">
                      <IconButton
                        onClick={() => onRowClick(log)}
                        color="primary"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <VisibilityRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {logs?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={pagination.total_count}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pagination.per_page}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};

export default LogTable;
